<template>
  <figure class="wipe" ref="root">
    <div class="wipe__pane wipe__pane--b">
      <slot name="after" />
      <span class="wipe__tag wipe__tag--r mono">{{ afterLabel }}</span>
    </div>
    <div class="wipe__pane wipe__pane--a" :style="{ clipPath: `inset(0 ${100 - pos}% 0 0)` }">
      <slot name="before" />
      <span class="wipe__tag wipe__tag--l mono">{{ beforeLabel }}</span>
    </div>

    <div class="wipe__handle" :style="{ left: pos + '%' }">
      <span class="wipe__line"></span>
      <button
        class="wipe__grip"
        type="button"
        role="slider"
        :aria-valuenow="Math.round(pos)"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="`Wipe between ${beforeLabel} and ${afterLabel}`"
        @pointerdown="onDown"
        @keydown.left.prevent="pos = Math.max(0, pos - 4)"
        @keydown.right.prevent="pos = Math.min(100, pos + 4)"
      >
        <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden="true">
          <path d="M6 1 1 6l5 5M12 1l5 5-5 5" stroke="currentColor" stroke-width="1.4" />
        </svg>
      </button>
    </div>
    <figcaption v-if="$slots.caption" class="wipe__cap"><slot name="caption" /></figcaption>
  </figure>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'

defineProps({
  beforeLabel: { type: String, default: 'before' },
  afterLabel: { type: String, default: 'after' }
})

const root = ref(null)
const pos = ref(52)
let dragging = false

function setFromEvent(e) {
  if (!root.value) return
  const r = root.value.getBoundingClientRect()
  pos.value = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100))
}
function onMove(e) {
  if (!dragging) return
  setFromEvent(e)
}
function onUp() {
  dragging = false
  window.removeEventListener('pointermove', onMove)
  window.removeEventListener('pointerup', onUp)
}
function onDown(e) {
  dragging = true
  setFromEvent(e)
  window.addEventListener('pointermove', onMove, { passive: true })
  window.addEventListener('pointerup', onUp)
}
onUnmounted(onUp)
</script>

<style scoped>
.wipe {
  position: relative;
  margin: 0;
  border: 1px solid var(--edge);
  border-radius: var(--radius);
  overflow: hidden;
  background: #0a0d12;
  user-select: none;
  touch-action: pan-y;
}
.wipe__pane { position: relative; }
.wipe__pane--a { position: absolute; inset: 0; }
.wipe__pane :deep(svg) { display: block; width: 100%; height: 100%; }

.wipe__tag {
  position: absolute;
  bottom: 12px;
  font-size: 9px;
  letter-spacing: 0.2em;
  color: var(--text-dim);
  background: rgba(4, 6, 10, 0.72);
  border: 1px solid var(--edge);
  padding: 5px 9px;
  border-radius: 2px;
}
.wipe__tag--l { left: 12px; }
.wipe__tag--r { right: 12px; }

.wipe__handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  z-index: 3;
}
.wipe__line {
  position: absolute;
  inset: 0 auto 0 -0.5px;
  width: 1px;
  background: linear-gradient(180deg, transparent, var(--signal) 18%, var(--signal) 82%, transparent);
}
.wipe__grip {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  padding: 0;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(6, 10, 16, 0.9);
  border: 1px solid var(--signal);
  color: var(--signal);
  cursor: ew-resize;
  box-shadow: 0 0 0 6px rgba(86, 225, 240, 0.08);
}
.wipe__cap {
  padding: 14px 16px;
  border-top: 1px solid var(--edge-soft);
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  color: var(--text-faint);
  line-height: 1.8;
}
</style>
